import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Dispatch } from "~/api/types";

/**
 * WHO answers on this desk, with WHICH model — and what is running right now.
 *
 * The header's model chooser writes the voice; `/talk` reads it for every turn it sends; any page
 * doing a model operation reports it through {@link useReportOperation} so the chip names the
 * model actually at work, not merely the one selected. With no page reporting, the chip falls
 * back to the person's own live IDE dispatches (a work step running in a window shows there).
 *
 * The model shown for an IDE turn is the one sent as `--model` on the dispatch — a concrete id,
 * so the CLI runs exactly that. "Window default" is said as such, never guessed.
 */

export type Voice =
  | {
      kind: "ide";
      sessionKey: string;
      /** "knowIt — MacBook". */
      label: string;
      workspacePath: string | null;
      /** The workspace the window is bound to (its repo files work there). */
      boundTeamId: string | null;
      /**
       * The workspace the window is REGISTERED in — its token acts there, so its answers land in
       * that workspace's conversation store. Talking to it happens from that workspace.
       */
      teamId?: string | null;
      teamName?: string | null;
      /** A concrete Claude model id, or null = the window's own default. */
      model: string | null;
      effort: string | null;
    }
  | {
      kind: "ai";
      thinkerId: string;
      label: string;
      model: string | null;
    };

/** The Claude models an IDE voice can be asked to use (newest first). */
export const CLAUDE_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "claude-fable-5-1", label: "Fable 5.1" },
  { id: "claude-opus-5-5", label: "Opus 5.5" },
  { id: "claude-sonnet-5-5", label: "Sonnet 5.5" },
  { id: "claude-haiku-4-5-20251001", label: "Haiku 4.5" },
];

export const EFFORTS = ["low", "medium", "high", "xhigh", "max"] as const;

/** "Opus 5.5" for a known id, the id itself otherwise. */
export function modelLabel(model: string | null | undefined): string | null {
  if (!model) return null;
  return CLAUDE_MODELS.find((m) => m.id === model)?.label ?? model;
}

export interface Operation {
  /** Stable per reporter, so a page replacing its own report never clobbers another's. */
  id: string;
  /** What is being done: "planning the feature", "work step CWAY-3 · build". */
  label: string;
  /** Who is doing it: "IDE knowIt". */
  voiceLabel: string;
  model: string | null;
  effort?: string | null;
  /** working = the model is on it; waiting = parked on a person (e.g. VS Code's consent card). */
  state: "working" | "waiting";
  detail?: string | null;
}

interface VoiceContextValue {
  voice: Voice | null;
  setVoice: (voice: Voice | null) => void;
  operations: Operation[];
  report: (operation: Operation | null, id: string) => void;
}

const VoiceContext = createContext<VoiceContextValue | null>(null);

const STORAGE_KEY = "docit.talk.voice";

function readStored(): Voice | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Voice;
    return parsed && (parsed.kind === "ide" || parsed.kind === "ai") ? parsed : null;
  } catch {
    return null;
  }
}

export function VoiceProvider({ children }: { children: ReactNode }) {
  const [voice, setVoiceState] = useState<Voice | null>(() =>
    typeof window === "undefined" ? null : readStored(),
  );
  const [operations, setOperations] = useState<Operation[]>([]);

  const setVoice = useCallback((next: Voice | null) => {
    setVoiceState(next);
    try {
      if (next) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage blocked: the choice lasts this tab, which is all a choice needs.
    }
  }, []);

  const report = useCallback((operation: Operation | null, id: string) => {
    setOperations((current) => {
      const rest = current.filter((op) => op.id !== id);
      return operation ? [...rest, { ...operation, id }] : rest;
    });
  }, []);

  const value = useMemo(
    () => ({ voice, setVoice, operations, report }),
    [voice, setVoice, operations, report],
  );
  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

const NO_VOICE: VoiceContextValue = {
  voice: null,
  setVoice: () => undefined,
  operations: [],
  report: () => undefined,
};

export function useVoice(): VoiceContextValue {
  return useContext(VoiceContext) ?? NO_VOICE;
}

/** Report (or clear, with null) this page's live model operation to the header chip. */
export function useReportOperation(id: string, operation: Operation | null) {
  const { report } = useVoice();
  const key = operation
    ? `${operation.label}|${operation.voiceLabel}|${operation.model}|${operation.effort}|${operation.state}|${operation.detail}`
    : "";
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the operation's identity.
  useEffect(() => {
    report(operation, id);
  }, [id, key, report]);
  useEffect(() => () => report(null, id), [id, report]);
}

function payloadOf(dispatch: Dispatch): Record<string, unknown> {
  if (!dispatch.payload) return {};
  try {
    const parsed = JSON.parse(dispatch.payload) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const LIVE_DETAIL = new Set(["queued", "running", "awaiting-approval"]);

/**
 * One of the person's own live dispatches as an operation — the chip's fallback when no page
 * reports one. `windows` maps a session key to its label.
 */
export function operationFromDispatch(
  dispatch: Dispatch,
  windows: Map<string, string>,
): Operation | null {
  const live = dispatch.statusDetail
    ? LIVE_DETAIL.has(dispatch.statusDetail)
    : ["queued", "claimed", "running"].includes(dispatch.status);
  if (!live || !dispatch.isOwn) return null;
  const payload = payloadOf(dispatch);
  const window =
    windows.get(dispatch.claimedBy ?? "") ?? windows.get(dispatch.target ?? "") ?? "your IDE";
  const surface = typeof payload.surface === "string" ? payload.surface : null;
  const itemKey = typeof payload.itemKey === "string" ? payload.itemKey : null;
  const stepTitle = typeof payload.stepTitle === "string" ? payload.stepTitle : null;
  const label =
    surface === "work-step" && itemKey
      ? `work step ${itemKey}${stepTitle ? ` · ${stepTitle}` : ""}`
      : dispatch.kind === "chat"
        ? "answering"
        : dispatch.kind === "flow"
          ? "running a flow"
          : "thinking";
  return {
    id: `dispatch:${dispatch.id}`,
    label,
    voiceLabel: `IDE ${window}`,
    model: dispatch.model ?? null,
    effort: dispatch.effort ?? null,
    state: dispatch.statusDetail === "awaiting-approval" ? "waiting" : "working",
    detail: dispatch.statusDetail,
  };
}
